import { gql, useMutation, useQuery } from '@apollo/client'
import { yupResolver } from '@hookform/resolvers/yup'
import AddIcon from '@mui/icons-material/Add'
import CancelIcon from '@mui/icons-material/Cancel'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Fab,
  FormHelperText,
  Grid2 as Grid,
  InputLabel,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import Modal from 'components/Modal'
import { useGetMe } from 'components/Session/hooks'
import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Company, PreQualifiedSite } from 'utils/__generated__/graphql'
import * as Yup from 'yup'

import { DATAROOM_BASE_QUERIES, DATAROOM_FRAGMENT } from '../constants'

const dataroomSchema = Yup.object({
  name: Yup.string().required('Please enter a name'),
  site: Yup.string().required('Please select a site'),
  targetCompanies: Yup.array()
    .of(Yup.string().required())
    .min(1, 'Please select at least one company')
    .required('Please select target companies'),
})

export type DataroomDataType = Yup.InferType<typeof dataroomSchema>

type DataroomFormProps = {
  sites: PreQualifiedSite[]
  companies: Company[]
  values?: DataroomDataType
  onDataroom: (data: DataroomDataType) => void
}

export function DataroomForm({
  sites,
  companies,
  onDataroom,
  values,
}: DataroomFormProps) {
  const { control, handleSubmit } = useForm({
    defaultValues: values || {
      name: '',
      site: sites[0]?.id || '',
      targetCompanies: [] as string[],
    },
    resolver: yupResolver(dataroomSchema),
  })

  const sortedSites = [...sites].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '')
  )

  const sortedCompanies = [...companies].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '')
  )

  return (
    <Box component="form" onSubmit={handleSubmit(onDataroom)}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Controller
            name="name"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <React.Fragment>
                <InputLabel htmlFor="name">
                  <Typography sx={theme => ({ ...theme.typography.caption })}>
                    Name*
                  </Typography>
                </InputLabel>
                <TextField
                  id="name"
                  error={!!error}
                  onChange={onChange}
                  value={value}
                  fullWidth
                  variant="outlined"
                  size="small"
                  helperText={error && error.message}
                />
              </React.Fragment>
            )}
          />
        </Grid>
        <Grid size={6}>
          <Controller
            name="site"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              const selectedSite = sortedSites.find(s => s.id === value) || null

              return (
                <React.Fragment>
                  <InputLabel>
                    <Typography sx={theme => ({ ...theme.typography.caption })}>
                      Site*
                    </Typography>
                  </InputLabel>
                  <Autocomplete
                    size="small"
                    options={sortedSites}
                    getOptionLabel={option => option.name || ''}
                    value={selectedSite}
                    onChange={(_, selected) => onChange(selected?.id || '')}
                    isOptionEqualToValue={(option, val) => option.id === val.id}
                    renderInput={params => (
                      <TextField
                        {...params}
                        error={!!error}
                        placeholder="Search sites..."
                      />
                    )}
                  />
                  {error && (
                    <FormHelperText error>{error.message}</FormHelperText>
                  )}
                </React.Fragment>
              )
            }}
          />
        </Grid>
        <Grid size={6}>
          <Controller
            name="targetCompanies"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              const selectedCompanies = sortedCompanies.filter(c =>
                (value || []).includes(c.id || '')
              )

              return (
                <React.Fragment>
                  <InputLabel>
                    <Typography sx={theme => ({ ...theme.typography.caption })}>
                      Target Companies*
                    </Typography>
                  </InputLabel>
                  <Autocomplete
                    multiple
                    size="small"
                    options={sortedCompanies}
                    getOptionLabel={option => option.name || ''}
                    value={selectedCompanies}
                    onChange={(_, selected) =>
                      onChange(selected.map(c => c.id || ''))
                    }
                    isOptionEqualToValue={(option, val) => option.id === val.id}
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option, index) => (
                        <Chip
                          size="small"
                          label={option.name}
                          {...getTagProps({ index })}
                          key={option.id}
                        />
                      ))
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        error={!!error}
                        placeholder="Search companies..."
                      />
                    )}
                  />
                  {error && (
                    <FormHelperText error>{error.message}</FormHelperText>
                  )}
                </React.Fragment>
              )
            }}
          />
        </Grid>
      </Grid>
      <Divider sx={{ mt: 1, mb: 1 }} />
      <Button
        type="submit"
        sx={{ float: 'right' }}
        variant="contained"
        disableElevation
      >
        Save
      </Button>
    </Box>
  )
}

const CREATE_DATAROOM = gql`
  ${DATAROOM_FRAGMENT}
  mutation CREATE_DATAROOM($dataroom: DataroomInput!) {
    createDataroom(dataroom: $dataroom) {
      ...DataroomFragment
    }
  }
`

function Create() {
  const [open, setOpen] = useState<boolean>(false)
  const { data: meData } = useGetMe()

  const theme = useTheme()

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const [createDataroom] = useMutation(CREATE_DATAROOM)

  const handleDataroom = (data: DataroomDataType) => {
    createDataroom({
      variables: {
        dataroom: {
          user: meData?.me?.id,
          company: meData?.me?.company?.id,
          isAdminDataroom: true,
          ...data,
        },
      },
      onCompleted: handleClose,
      update(cache, { data: { createDataroom } }) {
        cache.modify({
          fields: {
            findDatarooms(existingDatarooms = []) {
              const newDataroomRef = cache.writeFragment({
                data: createDataroom,
                fragment: gql`
                  fragment NewDataroom on Dataroom {
                    id
                  }
                `,
              })
              return [newDataroomRef, ...existingDatarooms]
            },
          },
        })
      },
    })
  }

  const { loading, error, data } = useQuery(DATAROOM_BASE_QUERIES, {
    variables: { siteFilter: { isPublished: true }, companyFilter: {} },
    skip: !open,
  })

  const sites = data?.sites || []
  const companies = data?.companies || []

  return (
    <React.Fragment>
      <Modal open={open} width="75%" maxWidth={480}>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography
            sx={{ ...theme.typography.h5, color: theme.palette.text.primary }}
          >
            Create Dataroom
          </Typography>
          <CancelIcon
            onClick={handleClose}
            sx={{ cursor: 'pointer', color: theme.palette.error.main }}
          />
        </Stack>
        <Divider sx={{ mt: 1, mb: 1 }} />
        {loading ? (
          <Stack sx={{ alignItems: 'center', p: 4 }}>
            <CircularProgress />
          </Stack>
        ) : error ? (
          <p>error...</p>
        ) : (
          <DataroomForm
            onDataroom={handleDataroom}
            sites={sites}
            companies={companies}
          />
        )}
      </Modal>
      <Fab
        color="primary"
        aria-label="add"
        onClick={handleOpen}
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
      >
        <AddIcon />
      </Fab>
    </React.Fragment>
  )
}

export default Create
